import { startTransition, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FolderOpen, Package, Shield } from 'lucide-react';
import { Spinner } from '@elka/ui';
import clsx from 'clsx';
import { useIsAdmin } from '@/features/auth/hooks/useIsAdmin';

export function SectionSwitcher({ hideAdmin }: { hideAdmin?: boolean } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const [isNavigating, setIsNavigating] = useState(false);

  // Determine current section from URL
  const currentSection = location.pathname.startsWith('/admin')
    ? 'admin'
    : location.pathname.startsWith('/rechesh')
      ? 'rechesh'
      : 'projects';

  // Reset navigating state when location changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsNavigating(false);
  }, [location.pathname]);

  const handleSectionChange = (section: 'projects' | 'rechesh' | 'admin') => {
    // Don't navigate if already on that section
    if (section === currentSection) {
      return;
    }

    setIsNavigating(true);

    // Use startTransition for smoother navigation
    startTransition(() => {
      if (section === 'projects') {
        navigate('/projects/clients');
      } else if (section === 'rechesh') {
        navigate('/rechesh/orders');
      } else {
        navigate('/admin/vacation-requests');
      }
    });
  };

  return (
    <div
      className={clsx(
        'flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 p-1 backdrop-blur-sm',
        hideAdmin && 'w-full'
      )}
    >
      <button
        onClick={() => handleSectionChange('projects')}
        disabled={isNavigating}
        className={clsx(
          'flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-[background-color,color,opacity,transform] active:scale-[0.96]',
          hideAdmin && 'flex-1',
          currentSection === 'projects'
            ? 'bg-white/20 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
          isNavigating && 'cursor-wait opacity-70'
        )}
      >
        {isNavigating && currentSection === 'rechesh' ? (
          <Spinner size="sm" className="text-inherit" />
        ) : (
          <FolderOpen size={18} />
        )}
        <span>פרויקטים</span>
      </button>

      <button
        onClick={() => handleSectionChange('rechesh')}
        disabled={isNavigating}
        className={clsx(
          'flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-[background-color,color,opacity,transform] active:scale-[0.96]',
          hideAdmin && 'flex-1',
          currentSection === 'rechesh'
            ? 'bg-white/20 text-white'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
          isNavigating && 'cursor-wait opacity-70'
        )}
      >
        {isNavigating && currentSection === 'projects' ? (
          <Spinner size="sm" className="text-inherit" />
        ) : (
          <Package size={18} />
        )}
        <span>רכש</span>
      </button>

      {/* Admin Section - Only visible to admins, hidden on mobile (accessed via user card) */}
      {isAdmin && !hideAdmin && (
        <button
          onClick={() => handleSectionChange('admin')}
          disabled={isNavigating}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-[background-color,color,opacity,transform] active:scale-[0.96]',
            currentSection === 'admin'
              ? 'bg-white/20 text-white'
              : 'text-white/70 hover:bg-white/10 hover:text-white',
            isNavigating && 'cursor-wait opacity-70'
          )}
        >
          {isNavigating && (currentSection === 'projects' || currentSection === 'rechesh') ? (
            <Spinner size="sm" className="text-inherit" />
          ) : (
            <Shield size={18} />
          )}
          <span>ניהול</span>
        </button>
      )}
    </div>
  );
}
